import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { TaskModal, PRIO_STYLE, Field, bLabel } from "./scouting/parts";
import { Plus, CalendarDays, X, Loader2, CheckCircle2 } from "lucide-react";
import { ReqImg } from "./portal/PortalRequests";

const STATUS_TABS = [
  { key: "open,scheduled,in_progress", tk: "wl.active", label: "Active" },
  { key: "open", tk: "wl.open", label: "Open" },
  { key: "scheduled", tk: "wl.scheduled", label: "Scheduled" },
  { key: "in_progress", tk: "wl.inprogress", label: "In Progress" },
  { key: "complete", tk: "wl.complete", label: "Complete" },
  { key: "cancelled", tk: "wl.cancelled", label: "Cancelled" },
];

export default function WorkList() {
  const { user } = useAuth();
  const { t: tr } = useI18n();
  const isManager = ["admin", "supervisor"].includes(user.role);
  const isAdmin = user.role === "admin";
  const [tasks, setTasks] = useState([]);
  const [meta, setMeta] = useState({ categories: [] });
  const [status, setStatus] = useState(() =>
    (user.role === "admin" && new URLSearchParams(window.location.search).get("tab") === "client_requests")
      ? "client_requests" : "open,scheduled,in_progress");
  const [cat, setCat] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [scheduling, setScheduling] = useState(null);
  const [requests, setRequests] = useState([]);

  const load = async () => {
    if (status === "client_requests") return;
    const { data } = await api.get(`/tasks?status=${status}`);
    setTasks(data);
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  useEffect(() => { api.get("/tasks/meta").then(({ data }) => setMeta(data)); }, []);
  const loadRequests = () => { if (isAdmin) api.get("/work-requests").then(({ data }) => setRequests(data)); };
  useEffect(() => { loadRequests(); }, []); // eslint-disable-line

  const approveRequest = async (r) => {
    await api.put(`/work-requests/${r.id}/approve`);
    loadRequests();
    load();
  };

  const setTaskStatus = async (t, s) => {
    await api.post(`/tasks/${t.id}/status`, { status: s });
    load();
  };

  const shown = useMemo(() => tasks.filter((t) => !cat || t.category === cat)
    .sort((a, b) => ({ urgent: 0, high: 1, normal: 2, low: 3 }[a.priority] ?? 2) - ({ urgent: 0, high: 1, normal: 2, low: 3 }[b.priority] ?? 2)), [tasks, cat]);

  const openRequests = useMemo(() => requests.filter((r) => r.status === "open")
    .sort((a, b) => ({ urgent: 0, high: 1, normal: 2, low: 3 }[a.priority] ?? 2) - ({ urgent: 0, high: 1, normal: 2, low: 3 }[b.priority] ?? 2)), [requests]);

  return (
    <div className="space-y-5" data-testid="worklist-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{tr("wl.title")}</h1>
          <p className="text-stone-500 text-sm mt-1">{tr("wl.sub")}</p>
        </div>
        <button data-testid="new-task-btn" onClick={() => setNewOpen(true)} className="h-14 px-6 bg-[#1F3D34] text-white font-bold uppercase flex items-center gap-2"><Plus size={18} /> {tr("wl.new")}</button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex overflow-x-auto border border-stone-200 bg-white">
          {STATUS_TABS.map((s) => (
            <button key={s.key} data-testid={`worklist-tab-${s.label.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setStatus(s.key)}
              className={`px-4 py-2.5 text-xs font-bold uppercase whitespace-nowrap ${status === s.key ? "bg-[#1F3D34] text-white" : "text-stone-600"}`}>{tr(s.tk)}</button>
          ))}
          {isAdmin && (
            <button data-testid="worklist-tab-client-requests" onClick={() => setStatus("client_requests")}
              className={`px-4 py-2.5 text-xs font-bold uppercase whitespace-nowrap ${status === "client_requests" ? "bg-[#1F3D34] text-white" : "text-stone-600"}`}>
              {tr("wl.client_requests")} ({openRequests.length})
            </button>
          )}
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-11 px-3 border border-stone-200 bg-white text-sm font-semibold">
          <option value="">{tr("wl.all_types")}</option>
          {meta.categories.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div className="space-y-2">
        {status === "client_requests" ? (
          <>
            {openRequests.length === 0 && (
              <div className="bg-white border border-stone-200 p-8 text-center text-stone-400 font-bold uppercase text-sm" data-testid="client-requests-empty-wl">
                {tr("wl.no_client_requests")}
              </div>
            )}
            {openRequests.map((r) => (
              <div key={r.id} className="bg-white border border-stone-200 p-4" data-testid={`wl-request-${r.id}`}>
                <div className="flex flex-wrap gap-3 items-start justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${PRIO_STYLE[r.priority] || "bg-stone-200"}`}>{r.priority}</span>
                      <span className="font-bold">{r.title}</span>
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-amber-100 text-amber-800">{tr("wl.from_client")}</span>
                    </div>
                    <div className="text-xs text-stone-500 mt-1">{r.requested_by} · {(r.ranch_numbers || []).join(", ")} · {(r.created_at || "").slice(0, 10)}</div>
                    {r.details && <div className="text-xs text-stone-500 mt-1 italic">{r.details}</div>}
                    {(r.photos || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {r.photos.map((p) => <ReqImg key={p.id} rid={r.id} pid={p.id} className="h-20 w-20 object-cover border border-stone-200" />)}
                      </div>
                    )}
                  </div>
                  <button data-testid={`approve-request-${r.id}`} onClick={() => approveRequest(r)}
                    className="h-10 px-4 text-xs font-bold uppercase bg-[#1F3D34] text-white flex items-center gap-1.5">
                    <CheckCircle2 size={14} /> {tr("wl.approve_to_worklist")}
                  </button>
                </div>
              </div>
            ))}
          </>
        ) : (
        <>
        {shown.length === 0 && <div className="bg-white border border-stone-200 p-8 text-center text-stone-400 font-bold uppercase text-sm">{tr("wl.none")}</div>}
        {shown.map((t) => (
          <div key={t.id} className="bg-white border border-stone-200 p-4 flex flex-wrap gap-3 items-center justify-between" data-testid={`task-row-${t.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${PRIO_STYLE[t.priority] || "bg-stone-200"}`}>{t.priority}</span>
                <span className="font-bold">{t.title}</span>
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-stone-100 text-stone-600">{t.category}</span>
                {t.source === "scouting" && <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-[#7C9673]/20 text-[#1F3D34]">{tr("wl.from_scouting")}</span>}
                {t.source === "client_request" && <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-amber-100 text-amber-800">{tr("wl.from_client")}</span>}
              </div>
              <div className="text-xs text-stone-500 mt-1">
                {[t.client_name, t.vineyard_name, t.block_labels?.length ? t.block_labels.map(bLabel).join(", ") : null,
                  t.acres ? `${t.acres} ac` : null, t.due_date ? `${tr("wl.due")} ${t.due_date}` : null].filter(Boolean).join(" · ")}
              </div>
              {(t.scheduled_date || t.supervisor_name || t.crew_name || t.employee_names?.length > 0 || t.equipment_names?.length > 0) && (
                <div className="text-xs text-[#1F3D34] font-semibold mt-1">
                  {[t.scheduled_date ? `📅 ${t.scheduled_date}${t.scheduled_time ? ` ${t.scheduled_time}` : ""}` : null,
                    t.supervisor_name ? `Sup: ${t.supervisor_name}` : null, t.crew_name ? `Crew: ${t.crew_name}` : null,
                    t.employee_names?.length ? t.employee_names.join(", ") : null,
                    t.equipment_names?.length ? t.equipment_names.join(", ") : null].filter(Boolean).join(" · ")}
                </div>
              )}
              {t.instructions && <div className="text-xs text-stone-500 mt-1 italic">{t.instructions}</div>}
              {t.status === "complete" && <div className="text-xs text-[#7C9673] font-bold mt-1">✓ {tr("wl.completed")} {t.completed_at?.slice(0, 10)} — {t.completed_by}</div>}
            </div>
            <div className="flex gap-2 flex-wrap">
              {["open", "scheduled"].includes(t.status) && isManager && (
                <button data-testid={`schedule-btn-${t.id}`} onClick={() => setScheduling(t)} className="h-10 px-3 text-xs font-bold uppercase border border-[#1F3D34] text-[#1F3D34] flex items-center gap-1"><CalendarDays size={14} /> {t.status === "scheduled" ? tr("wl.reschedule") : tr("wl.schedule")}</button>
              )}
              {["open", "scheduled"].includes(t.status) && (
                <button onClick={() => setTaskStatus(t, "in_progress")} className="h-10 px-3 text-xs font-bold uppercase border border-stone-300">{tr("wl.start")}</button>
              )}
              {t.status !== "complete" && t.status !== "cancelled" && (
                <button data-testid={`complete-btn-${t.id}`} onClick={() => setTaskStatus(t, "complete")} className="h-10 px-3 text-xs font-bold uppercase bg-[#1F3D34] text-white">{tr("wl.complete_btn")}</button>
              )}
              {isManager && t.status !== "cancelled" && t.status !== "complete" && (
                <button onClick={() => setTaskStatus(t, "cancelled")} className="h-10 px-3 text-xs font-bold uppercase border border-stone-300 text-stone-400">{tr("wl.cancel")}</button>
              )}
            </div>
          </div>
        ))}
        </>
        )}
      </div>

      <TaskModal open={newOpen} onClose={() => setNewOpen(false)} onCreated={load} />
      {scheduling && <ScheduleModal task={scheduling} onClose={() => setScheduling(null)} onDone={() => { setScheduling(null); load(); }} />}
    </div>
  );
}

function ScheduleModal({ task, onClose, onDone }) {
  const { t } = useI18n();
  const [employees, setEmployees] = useState([]);
  const [crews, setCrews] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [f, setF] = useState({
    scheduled_date: task.scheduled_date || "", scheduled_time: task.scheduled_time || "",
    supervisor_id: task.supervisor_id || "", crew_id: task.crew_id || "",
    employee_ids: task.employee_ids || [], equipment_ids: task.equipment_ids || [],
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      const [e, c, q] = await Promise.all([api.get("/employees"), api.get("/crews"), api.get("/equipment")]);
      setEmployees(e.data.filter((x) => x.active)); setCrews(c.data); setEquipment(q.data.filter((x) => x.active));
    })();
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const supervisor = employees.find((e) => e.id === f.supervisor_id);
      const crew = crews.find((c) => c.id === f.crew_id);
      await api.post(`/tasks/${task.id}/schedule`, { ...f,
        supervisor_name: supervisor?.name || null, crew_name: crew?.name || null,
        employee_names: employees.filter((e) => f.employee_ids.includes(e.id)).map((e) => e.name),
        equipment_names: equipment.filter((e) => f.equipment_ids.includes(e.id)).map((e) => `${e.code || ""} ${e.name}`.trim()),
      });
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
      <div className="bg-white border border-stone-200 w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="schedule-modal">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>{t("wl.schedule")} — {task.title}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("wl.date")}><input type="date" data-testid="schedule-date-input" value={f.scheduled_date} onChange={(e) => setF({ ...f, scheduled_date: e.target.value })} className="w-full h-12 px-3 border border-stone-300" /></Field>
          <Field label={t("wl.time")}><input type="time" value={f.scheduled_time} onChange={(e) => setF({ ...f, scheduled_time: e.target.value })} className="w-full h-12 px-3 border border-stone-300" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("wl.supervisor")}>
            <select value={f.supervisor_id} onChange={(e) => setF({ ...f, supervisor_id: e.target.value })} className="w-full h-12 px-3 border border-stone-300">
              <option value="">—</option>
              {employees.filter((e) => ["Supervisor", "Foreman", "Admin"].includes(e.category)).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </Field>
          <Field label={t("wl.crew")}>
            <select value={f.crew_id} onChange={(e) => setF({ ...f, crew_id: e.target.value })} className="w-full h-12 px-3 border border-stone-300">
              <option value="">—</option>
              {crews.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label={t("wl.employees")}>
          <select multiple value={f.employee_ids} onChange={(e) => setF({ ...f, employee_ids: [...e.target.selectedOptions].map((o) => o.value) })} className="w-full px-3 py-2 border border-stone-300 h-28">
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.category})</option>)}
          </select>
        </Field>
        <Field label={t("wl.equipment")}>
          <select multiple value={f.equipment_ids} onChange={(e) => setF({ ...f, equipment_ids: [...e.target.selectedOptions].map((o) => o.value) })} className="w-full px-3 py-2 border border-stone-300 h-24">
            {equipment.map((e) => <option key={e.id} value={e.id}>{e.code ? `${e.code} · ` : ""}{e.name}</option>)}
          </select>
        </Field>
        <button data-testid="schedule-save-btn" onClick={save} disabled={busy || !f.scheduled_date}
          className="w-full h-14 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40 flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} {t("wl.schedule_task")}
        </button>
      </div>
    </div>
  );
}
